import React from 'react';
import { TranslationKey } from './../../../types/translation/TranslationKey';
import { Asset } from '../../../types/asset/asset';
import { ProductMasterData } from '../../../types/product_master_data/productMasterData';
import { t, translate } from '../../../types/translation/Translator';
import { User } from '../../../types/user/user';
import { toMap } from '../../../util/map.util';
import { TableHeader } from '../../components/Table';
import AssetStatusLabel from './AssetStatusLabel';
import { CustomFieldUtil } from '../../../types/custom_field/customField.util';
import { toFilterString } from '../../../util/string.util';
import { AssetUtil } from '../../../types/asset/asset.util';
import { AssetTransaction } from '../../../types/asset_transaction/assetTransaction';
import { UnitOfMeasureUtil } from '../../../types/uom';
import { TableSettings } from '../../../types/table_settings/TableSettings';
import { AssetTableSettingsColumn } from '../../../types/table_settings/asset/AssetTableSettings';
import { CustomFieldType } from '../../../types/custom_field';
import dayjs from 'dayjs';
import { Company } from '../../../types/company';

// We should split user assets & asset management table
export namespace AssetTable {
  export const header = (
    productMasterData: Map<string, ProductMasterData>,
    users: Map<string, User>,
    settings: TableSettings<any>,
    userDetail: boolean = false,
    locale: string,
    company: Company,
  ) => {
    const wantedColumns = toMap(settings.columns, 'column');

    const columns: TableHeader<Asset>[] = [];

    if (company.partner) {
      columns.push({
        key: 'company',
        name: translate({ key: TranslationKey.company }),
        text: (item: Asset) => company.name,
      });
    }

    if (wantedColumns.has(AssetTableSettingsColumn.productName)) {
      columns.push({
        key: 'productName',
        name: wantedColumns.get(AssetTableSettingsColumn.productName)?.label || '',
        text: (item: Asset) =>
          productMasterData.get(item.product.productMasterDataId)?.productName ||
          t(TranslationKey.unknownProductMasterData).singular.label,
        column: AssetTableSettingsColumn.productName,
      });
    }

    if (wantedColumns.has(AssetTableSettingsColumn.productNumber)) {
      columns.push({
        key: 'productNumber',
        name: wantedColumns.get(AssetTableSettingsColumn.productNumber)?.label || '',
        text: (item: Asset) =>
          productMasterData.get(item.product.productMasterDataId)?.productNumber ||
          t(TranslationKey.unknownProductMasterData).singular.label,
        column: AssetTableSettingsColumn.productNumber,
      });
    }

    if (wantedColumns.has(AssetTableSettingsColumn.availableQuantity)) {
      columns.push({
        key: 'availableQuantity',
        name: wantedColumns.get(AssetTableSettingsColumn.availableQuantity)?.label || '',
        text: (item: Asset) => {
          const pmd = productMasterData.get(item.product.productMasterDataId);
          return `${item.product.quantity - (item.product.reservedQuantity || 0)}/${
            item.product.quantity
          } ${UnitOfMeasureUtil.getSuffix(pmd?.unitOfMeasure)}`;
        },
        column: AssetTableSettingsColumn.availableQuantity,
      });
    }

    if (wantedColumns.has(AssetTableSettingsColumn.quantity)) {
      columns.push({
        key: 'quantity',
        name: wantedColumns.get(AssetTableSettingsColumn.quantity)?.label || '',
        text: (item: Asset) => item.product.quantity.toString(),
        column: AssetTableSettingsColumn.quantity,
      });
    }

    if (wantedColumns.has(AssetTableSettingsColumn.assignedTo) && !userDetail) {
      columns.push({
        key: 'assignedTo',
        name: wantedColumns.get(AssetTableSettingsColumn.assignedTo)?.label || '',
        text: (item: Asset) => users.get(item.location?.entityId || '')?.email || '',
        column: AssetTableSettingsColumn.assignedTo,
      });
    }

    if (wantedColumns.has(AssetTableSettingsColumn.serial)) {
      columns.push({
        key: 'serial',
        name: wantedColumns.get(AssetTableSettingsColumn.serial)?.label || '',
        text: (item: Asset) => item.product.serial || '',
        column: AssetTableSettingsColumn.serial,
      });
    }

    if (wantedColumns.has(AssetTableSettingsColumn.status) && !userDetail) {
      columns.push({
        key: 'status',
        name: wantedColumns.get(AssetTableSettingsColumn.status)?.label || '',
        text: (item: Asset) => <AssetStatusLabel asset={item} />,
        column: AssetTableSettingsColumn.status,
      });
    }

    const defaultKeys = new Set(Object.keys(AssetTableSettingsColumn));
    const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

    customFieldColumns.forEach(item => {
      columns.push({
        key: item.column,
        name: item.label,
        sortValue: (asset: Asset) => {
          const customFieldValue = asset.product.customFieldValues().find(i => i.name === item.column);
          if (!customFieldValue) return '';
          if (customFieldValue.type === CustomFieldType.date) return dayjs(customFieldValue.value);
          return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
        },
        text: (asset: Asset) => {
          const customFieldValue = asset.product.customFieldValues().find(i => i.name === item.column);
          if (!customFieldValue) return '';
          return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
        },
        column: item.column,
      });
    });

    return columns.sort(
      (a: TableHeader<Asset>, b: TableHeader<Asset>) =>
        (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
    );
  };

  export const filter = (
    item: Asset,
    filter: string,
    productMasterData: Map<string, ProductMasterData>,
    users: Map<string, User>,
    assets: Map<string, Asset>,
    assetTransactions: Map<string, AssetTransaction>,
    settings: TableSettings<any>,
  ) => {
    filter = toFilterString(filter);

    const wantedColumns = toMap(settings.columns, 'column');

    const productName = toFilterString(productMasterData.get(item.product.productMasterDataId)?.productName);
    const productNumber = toFilterString(productMasterData.get(item.product.productMasterDataId)?.productNumber);

    const user = users.get(item.location?.entityId || '');
    const firstName = toFilterString(user?.firstName);
    const lastName = toFilterString(user?.lastName);
    const email = toFilterString(user?.email);

    const serial = toFilterString(item.product.serial);

    if (wantedColumns.has(AssetTableSettingsColumn.productName) && productName?.includes(filter)) {
      return true;
    }

    if (wantedColumns.has(AssetTableSettingsColumn.productNumber) && productNumber?.includes(filter)) {
      return true;
    }

    if (
      wantedColumns.has(AssetTableSettingsColumn.assignedTo) &&
      (firstName.includes(filter) || lastName.includes(filter) || email.includes(filter))
    ) {
      return true;
    }

    if (
      wantedColumns.has(AssetTableSettingsColumn.status) &&
      AssetUtil.statusToString(item, assets, assetTransactions).includes(filter)
    ) {
      return true;
    }

    if (wantedColumns.has(AssetTableSettingsColumn.serial) && serial.includes(filter)) {
      return true;
    }

    const defaultKeys = new Set(Object.keys(AssetTableSettingsColumn));
    const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

    for (const custom of customFieldColumns) {
      const field = item.product
        .customFieldValues()
        .find(i => i.name === custom.column)
        ?.value.toLocaleLowerCase();
      if (field?.includes(filter)) return true;
    }

    return false;
  };
}
